import React, { FC, useEffect, useState } from "react";

import log from "loglevel";
import { useTranslation } from "react-i18next";

import { LoaderWithText } from "@/components/atoms";
import config from "@/config";
import { authApi, userApi } from "@/shared/apis";
import { UserState } from "@/shared/domain";
import { ItsmeUserInfo } from "@/shared/domain/auth";
import { useAuth, useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import ChooseBroker from "../../ChooseBroker/ChooseBroker";

// URL examples
// https://mybroker/itsme?state=1234567890 >> received from itsme external app
// https://mybroker/itsme?o=89002&state=1234567890 >> received from itsme external app

const itsmeContextDefault = {
    itsmeId: null,
    itsmeToken: null,
    email: null,
    isLoading: true,
};

const ItsMe: FC = () => {
    const {
        globalState: { officeId },
        search: { state: token },
    } = useRouterHook();
    const { navToRoot: navToRootOriginal, navToRegisterUnknownItsme } = useNavigation();
    const { t } = useTranslation();
    const { authItsme, prepare } = useAuth();
    const [itsmeContext, setItsmeContext] = useState(itsmeContextDefault);

    const navToRoot = () => {
        navToRootOriginal(undefined, { replace: true });
    };

    const handleItsmeAuthentication = async (userInfo: ItsmeUserInfo) => {
        try {
            // Step 1: authenticate (get cookie & token based on user/password)
            await authItsme(userInfo.itsmeId, userInfo.itsmeToken);
            tagManager.pushLoginEvent("itsme");

            log.info("authItsme success");

            setItsmeContext({
                itsmeId: userInfo.itsmeId,
                itsmeToken: userInfo.itsmeToken,
                email: userInfo.email,
                isLoading: false,
            });
        } catch (e) {
            log.warn("ItsmeAuthentication failed", e.message);
            navToRoot();
            if (config.NODE_ENV !== "test") {
                throw e;
            }
        }
    };

    const handleItsmeProcess = async () => {
        try {
            // Step 1: get itsme info with received token
            log.info("fItsmePost", { token });
            const userInfo = await authApi.fItsmePost(token);
            // if the user is not registered on my broker, redirect to screen where he can link of register
            if (!userInfo.itsmeToken) {
                navToRegisterUnknownItsme(userInfo, officeId);
                return;
            }

            // Step 2: get the associated itsme email
            log.info("itsme info", { itsmeId: userInfo.itsmeId });
            const resInfo = await userApi.itsmeInfo(userInfo.itsmeId);
            log.info("itsme info success");
            userInfo.email = resInfo.email;
            if (!resInfo.email) {
                navToRegisterUnknownItsme(userInfo, officeId);
                return;
            }

            // Step 3: check the status of the associated itsme email
            log.info("user status", { email: userInfo.email });
            const resStatus: MyBrokerApi.UserStatus = await userApi.getUserStatus(userInfo.email);
            log.info("user status success");

            // if email status is unknown then redirect to the intermediary screen to register or link
            if (resStatus.state === UserState.UNKNOWN) {
                navToRegisterUnknownItsme(userInfo, officeId);
                return;
            }

            // Step 4: continue to auto-login
            prepare(resStatus.state as UserState);
            await handleItsmeAuthentication(userInfo);
        } catch (e) {
            navToRoot();
            if (config.NODE_ENV !== "test") {
                throw e;
            }
        }
    };

    useEffect(() => {
        if (token) {
            log.info("ItsMe login started");
            handleItsmeProcess();
            return;
        }

        // If no token is received, redirect the user to the login page
        navToRoot();
    }, []);

    return (
        <>
            {itsmeContext.isLoading ? (
                <LoaderWithText text={t("ItsMe.LoadingMessage")} />
            ) : (
                <ChooseBroker email={itsmeContext.email} fromItsMe />
            )}
        </>
    );
};

export default ItsMe;
